'use client';
import React from 'react';
import Link from 'next/link';
import { ImageGallery as SharedImageGallery } from '@launchstore/shared-puck';
import { useProduct } from '@/lib/hooks/useProduct';
import { usePuckContext } from '@/components/puck/PuckContextProvider';
import type { ProductDataImage } from '@launchstore/shared-puck';

export type ProductImageGalleryProps = import('@launchstore/shared-puck').ImageGalleryProps;

const SharedImageGalleryRender = (SharedImageGallery as any).render;

const ASPECT: Record<string, string> = {
  square: 'aspect-square',
  portrait: 'aspect-[3/4]',
  landscape: 'aspect-[4/3]',
  wide: 'aspect-[16/9]',
  '1:1': 'aspect-square',
  '3:4': 'aspect-[3/4]',
  '4:3': 'aspect-[4/3]',
  '16:9': 'aspect-[16/9]',
};

/**
 * Card mode — the shared gallery is built for the product PAGE (500px
 * container, thumbnails, zoom). Inside a product card we render a compact,
 * aspect-locked image that links to the product page, with the second image
 * revealed on hover when one exists.
 */
const CardGallery: React.FC<{ props: any; images: ProductDataImage[]; title?: string; href: string }> = ({ props, images, title, href }) => {
  if (!images.length) {
    return (
      <div className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
        <span className="text-gray-400 text-sm">No image</span>
      </div>
    );
  }
  // The shared gallery's aspect field is `mainImageAspectRatio`; older
  // templates may still carry the legacy `imageRatio` prop. Square (1:1) is
  // the e-commerce standard for card grids and the fallback.
  const ratio = props.mainImageAspectRatio || props.imageRatio;
  const aspect = ASPECT[ratio] || 'aspect-square';
  const radius = props.borderRadius && props.borderRadius !== 'none' ? `rounded-${props.borderRadius}` : 'rounded-lg';
  const primary = images[0];
  const secondary = images[1];

  return (
    <Link href={href} className="group block relative w-full overflow-hidden bg-gray-100">
      <div className={`${aspect} ${radius} w-full`}>
        <img
          src={primary.url}
          alt={primary.alt || title || 'Product image'}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300 group-hover:opacity-0"
          loading="lazy"
        />
        {secondary && (
          <img
            src={secondary.url}
            alt={secondary.alt || title || 'Product image'}
            className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-300 group-hover:opacity-100"
            loading="lazy"
          />
        )}
      </div>
    </Link>
  );
};

const ProductImageGalleryImpl: React.FC<ProductImageGalleryProps> = (props: any) => {
  const { product } = useProduct() as any;
  const images: ProductDataImage[] = (product?.images || []).map((img: any, i: number) => ({
    id: img.id || String(i),
    url: img.url,
    alt: img.alt || `${product?.title || 'Product'} - Image ${i + 1}`,
  }));

  const puckContext = (usePuckContext() as any)?.context;
  if (puckContext?.productCard && product?.handle) {
    const countryCode = puckContext.countryCode || 'us';
    return (
      <CardGallery
        props={props}
        images={images}
        title={product?.title}
        href={`/${countryCode}/products/${product.handle}`}
      />
    );
  }

  return <SharedImageGalleryRender {...(props as any)} images={images} productTitle={product?.title} />;
};

export const ProductImageGallery: any = { ...SharedImageGallery, render: (props: any) => <ProductImageGalleryImpl {...props} /> };
export default ProductImageGallery;
